import { useState, useEffect } from "react";
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subDays,
  subWeeks,
  subMonths,
  isSameDay,
  getYear,
} from "date-fns";
import { Calendar } from "./calendar";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import type { DateRange } from "react-day-picker";

export interface DateRangePickerProps {
  mode?: "single" | "range";
  value?: Date | DateRange;
  onChange?: (value: Date | DateRange | undefined) => void;
  placeholder?: string;
  className?: string;
}

// Helper functions for predefined ranges
const getTodayRange = (): DateRange => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return { from: today, to: today };
};

const getYesterdayRange = (): DateRange => {
  const yesterday = subDays(new Date(), 1);
  yesterday.setHours(0, 0, 0, 0);
  return { from: yesterday, to: yesterday };
};

const getThisWeekRange = (): DateRange => {
  const today = new Date();
  return {
    from: startOfWeek(today, { weekStartsOn: 1 }), // Monday
    to: endOfWeek(today, { weekStartsOn: 1 }),
  };
};

const getLastWeekRange = (): DateRange => {
  const lastWeek = subWeeks(new Date(), 1);
  return {
    from: startOfWeek(lastWeek, { weekStartsOn: 1 }),
    to: endOfWeek(lastWeek, { weekStartsOn: 1 }),
  };
};

const getLast7DaysRange = (): DateRange => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const sevenDaysAgo = subDays(today, 6);
  return { from: sevenDaysAgo, to: today };
};

const getLast30DaysRange = (): DateRange => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const thirtyDaysAgo = subDays(today, 29);
  return { from: thirtyDaysAgo, to: today };
};

const getThisMonthRange = (): DateRange => {
  const today = new Date();
  return {
    from: startOfMonth(today),
    to: endOfMonth(today),
  };
};

const getLastMonthRange = (): DateRange => {
  const lastMonth = subMonths(new Date(), 1);
  return {
    from: startOfMonth(lastMonth),
    to: endOfMonth(lastMonth),
  };
};

export function DateRangePicker({
  mode = "range",
  value,
  onChange,
  placeholder,
  className,
}: DateRangePickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Initialize state from value prop
  const getInitialRange = (): DateRange | undefined => {
    if (!value) return undefined;
    if (value instanceof Date) {
      return { from: value, to: value };
    }
    if (typeof value === "object" && "from" in value) {
      return value;
    }
    return undefined;
  };

  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(
    getInitialRange()
  );
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    value && value instanceof Date ? value : undefined
  );

  // Temporary state for calendar selection (not applied until "Set" is clicked)
  const [tempSelectedRange, setTempSelectedRange] = useState<
    DateRange | undefined
  >(getInitialRange());
  const [tempSelectedDate, setTempSelectedDate] = useState<Date | undefined>(
    value && value instanceof Date ? value : undefined
  );

  // Sync with value prop changes
  useEffect(() => {
    const newRange = getInitialRange();
    setSelectedRange(newRange);
    setTempSelectedRange(newRange);
    if (value instanceof Date) {
      setSelectedDate(value);
      setTempSelectedDate(value);
    } else {
      setSelectedDate(undefined);
      setTempSelectedDate(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handlePredefinedRange = (rangeFn: () => DateRange) => {
    const range = rangeFn();
    // Only update temporary state, not the main state
    // Main state will be updated when "Set" is clicked
    if (mode === "single") {
      setTempSelectedDate(range.from);
      setTempSelectedRange(undefined);
    } else {
      setTempSelectedRange(range);
      setTempSelectedDate(undefined);
    }
  };

  const handleCalendarSelect = (range: DateRange | undefined) => {
    // Only update temporary state, not the main state
    if (mode === "single" && range?.from) {
      setTempSelectedDate(range.from);
      setTempSelectedRange(undefined);
    } else {
      setTempSelectedRange(range);
      setTempSelectedDate(undefined);
    }
  };

  const handleSet = () => {
    // Apply temporary state to main state when "Set" is clicked
    if (mode === "single") {
      setSelectedDate(tempSelectedDate);
      setSelectedRange(undefined);
      onChange?.(tempSelectedDate);
    } else {
      setSelectedRange(tempSelectedRange);
      setSelectedDate(undefined);
      onChange?.(tempSelectedRange);
    }
    setOpen(false);
  };

  const handleClear = () => {
    setSelectedRange(undefined);
    setSelectedDate(undefined);
    onChange?.(undefined);
  };

  const predefinedRanges = [
    { key: "today", label: t("datePicker.today"), fn: getTodayRange },
    {
      key: "yesterday",
      label: t("datePicker.yesterday"),
      fn: getYesterdayRange,
    },
    { key: "thisWeek", label: t("datePicker.thisWeek"), fn: getThisWeekRange },
    { key: "lastWeek", label: t("datePicker.lastWeek"), fn: getLastWeekRange },
    {
      key: "last7Days",
      label: t("datePicker.last7Days"),
      fn: getLast7DaysRange,
    },
    {
      key: "last30Days",
      label: t("datePicker.last30Days"),
      fn: getLast30DaysRange,
    },
    {
      key: "thisMonth",
      label: t("datePicker.thisMonth"),
      fn: getThisMonthRange,
    },
    {
      key: "lastMonth",
      label: t("datePicker.lastMonth"),
      fn: getLastMonthRange,
    },
  ];

  // Main range for display (used in displayValue)
  const currentRange =
    mode === "single"
      ? selectedDate
        ? { from: selectedDate, to: selectedDate }
        : undefined
      : selectedRange;

  // Temporary range for calendar display (used in Calendar selected prop)
  const tempCurrentRange =
    mode === "single"
      ? tempSelectedDate
        ? { from: tempSelectedDate, to: tempSelectedDate }
        : undefined
      : tempSelectedRange;

  // Check if a predefined range is selected
  const isPredefinedRangeSelected = (rangeFn: () => DateRange): boolean => {
    const range = rangeFn();
    if (!currentRange?.from || !range.from) return false;

    if (mode === "single") {
      return isSameDay(currentRange.from, range.from);
    }

    if (!currentRange.to || !range.to) return false;
    return (
      isSameDay(currentRange.from, range.from) &&
      isSameDay(currentRange.to, range.to)
    );
  };

  const displayValue = () => {
    // Check if a predefined range is selected
    const selectedPredefinedRange = predefinedRanges.find((range) =>
      isPredefinedRangeSelected(range.fn)
    );

    if (selectedPredefinedRange) {
      return selectedPredefinedRange.label;
    }

    if (mode === "single") {
      if (selectedDate) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const selected = new Date(selectedDate);
        selected.setHours(0, 0, 0, 0);
        if (isSameDay(selected, today)) {
          return t("datePicker.today");
        }

        // Custom format: abbreviated month, no year if current year
        const currentYear = getYear(new Date());
        const selectedYear = getYear(selected);
        const month = format(selected, "MMM");
        const day = format(selected, "d");

        if (selectedYear === currentYear) {
          return `${month} ${day}`;
        } else {
          return `${month} ${day}, ${selectedYear}`;
        }
      }
      return placeholder || t("datePicker.startDate");
    } else {
      if (selectedRange?.from && selectedRange?.to) {
        const from = new Date(selectedRange.from);
        const to = new Date(selectedRange.to);
        from.setHours(0, 0, 0, 0);
        to.setHours(0, 0, 0, 0);
        if (isSameDay(from, to)) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (isSameDay(from, today)) {
            return t("datePicker.today");
          }

          // Custom format: abbreviated month, no year if current year
          const currentYear = getYear(new Date());
          const fromYear = getYear(from);
          const month = format(from, "MMM");
          const day = format(from, "d");

          if (fromYear === currentYear) {
            return `${month} ${day}`;
          } else {
            return `${month} ${day}, ${fromYear}`;
          }
        }

        // Custom format: abbreviated month, no year if current year
        const currentYear = getYear(new Date());
        const fromYear = getYear(from);
        const toYear = getYear(to);
        const fromMonth = format(from, "MMM");
        const toMonth = format(to, "MMM");
        const fromDay = format(from, "d");
        const toDay = format(to, "d");

        // If same year and same month: "Jan 6 - 14"
        if (fromYear === toYear && fromMonth === toMonth) {
          if (fromYear === currentYear) {
            return `${fromMonth} ${fromDay} - ${toDay}`;
          } else {
            return `${fromMonth} ${fromDay}, ${fromYear} - ${toDay}`;
          }
        }

        // If same year but different months: "Jan 6 - Feb 14"
        if (fromYear === toYear) {
          if (fromYear === currentYear) {
            return `${fromMonth} ${fromDay} - ${toMonth} ${toDay}`;
          } else {
            return `${fromMonth} ${fromDay}, ${fromYear} - ${toMonth} ${toDay}`;
          }
        }

        // Different years: "Jan 6, 2025 - Feb 14, 2026"
        return `${fromMonth} ${fromDay}, ${fromYear} - ${toMonth} ${toDay}, ${toYear}`;
      }
      if (selectedRange?.from) {
        // Custom format: abbreviated month, no year if current year
        const currentYear = getYear(new Date());
        const fromYear = getYear(selectedRange.from);
        const month = format(selectedRange.from, "MMM");
        const day = format(selectedRange.from, "d");

        if (fromYear === currentYear) {
          return `${month} ${day}`;
        } else {
          return `${month} ${day}, ${fromYear}`;
        }
      }
      return placeholder || t("datePicker.startDate");
    }
  };

  // Sync temp state with main state when popover opens
  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      // When opening, sync temp state with main state
      if (mode === "single") {
        setTempSelectedDate(selectedDate);
        setTempSelectedRange(undefined);
      } else {
        setTempSelectedRange(selectedRange);
        setTempSelectedDate(undefined);
      }
    }
    setOpen(newOpen);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "h-[40px] w-auto min-w-[100px] border-[#e4e4e7] bg-white pl-[12px] pr-[12px] gap-[8px] justify-start text-left font-normal",
            !currentRange && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="h-[16px] w-[16px] text-[#09090b] shrink-0" />
          <span className="flex-1 truncate !font-600 text-[#09090b]">
            {displayValue()}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 mr-[22px] bg-white" align="start">
        <div className="flex flex-col">
          {/* Top Input Fields */}
          <div className="flex gap-2 p-4 border-b border-[#e4e4e7]">
            <div className="flex-1">
              <div className="relative">
                <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#09090b] pointer-events-none" />
                <Input
                  className="pl-9 pr-9 h-[40px] text-sm !font-600 border-[#e4e4e7] bg-white"
                  placeholder={t("datePicker.startDate")}
                  value={
                    tempCurrentRange?.from
                      ? (() => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const from = new Date(tempCurrentRange.from);
                          from.setHours(0, 0, 0, 0);
                          if (isSameDay(from, today)) {
                            return t("datePicker.today");
                          }
                          return format(tempCurrentRange.from, "PPP");
                        })()
                      : ""
                  }
                  readOnly
                />
                {tempCurrentRange?.from && (
                  <X
                    className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 cursor-pointer opacity-50 hover:opacity-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (mode === "single") {
                        setTempSelectedDate(undefined);
                        setTempSelectedRange(undefined);
                      } else {
                        setTempSelectedRange({
                          from: undefined,
                          to: tempCurrentRange.to,
                        });
                      }
                    }}
                  />
                )}
              </div>
            </div>
            {mode === "range" && (
              <div className="flex-1">
                <div className="relative">
                  <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#09090b] pointer-events-none" />
                  <Input
                    className="pl-9 pr-9 h-[40px] text-sm border-[#e4e4e7] bg-white"
                    placeholder={t("datePicker.endDate")}
                    value={
                      tempCurrentRange?.to
                        ? format(tempCurrentRange.to, "PPP")
                        : ""
                    }
                    readOnly
                  />
                  {tempCurrentRange?.to && (
                    <X
                      className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 cursor-pointer opacity-50 hover:opacity-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        setTempSelectedRange({
                          from: tempCurrentRange.from,
                          to: undefined,
                        });
                      }}
                    />
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Bottom: Predefined Ranges + Calendar */}
          <div className="flex">
            {/* Left Panel: Predefined Ranges */}
            <div className="border-r border-[#e4e4e7] p-4 min-w-[160px] bg-white">
              <div className="flex flex-col gap-1">
                {predefinedRanges.map((range) => {
                  const isSelected = isPredefinedRangeSelected(range.fn);

                  return (
                    <button
                      key={range.key}
                      onClick={() => handlePredefinedRange(range.fn)}
                      className={cn(
                        "text-left text-sm text-[#09090b] hover:bg-[#f4f4f5] px-2 py-1.5 rounded transition-colors",
                        isSelected && "bg-[#f4f4f5] font-medium"
                      )}
                    >
                      {range.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right Panel: Calendar */}
            <div className="flex flex-col">
              <div className="p-4 bg-white">
                {mode === "single" ? (
                  <Calendar
                    mode="single"
                    selected={tempCurrentRange?.from}
                    onSelect={(date) => {
                      if (date) {
                        handleCalendarSelect({ from: date, to: date });
                      } else {
                        handleCalendarSelect(undefined);
                      }
                    }}
                    numberOfMonths={1}
                    className="rounded-md"
                  />
                ) : (
                  <Calendar
                    mode="range"
                    selected={tempCurrentRange}
                    onSelect={handleCalendarSelect}
                    numberOfMonths={1}
                    className="rounded-md"
                  />
                )}
              </div>

              {/* Set Button */}
              <div className="flex justify-end p-4 border-t border-[#e4e4e7] bg-white">
                <Button
                  onClick={handleSet}
                  className="h-[36px] px-6 bg-[#09090b] text-white hover:bg-[#09090b]/90 rounded-md"
                >
                  {t("datePicker.set")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
